import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const MailIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const programs = [
  {
    grade: '3-K',
    eligibility: 'Children who turn three during the current calendar year (January 1 – December 31).',
    description: 'A nurturing, play-based first classroom experience where our youngest Lions develop social, emotional, and early literacy skills through guided exploration.',
    highlights: ['15 students per class', 'One head teacher + one paraprofessional', 'Born in 2023 for Fall 2026'],
  },
  {
    grade: 'Pre-K',
    eligibility: 'Children who turn four during the current calendar year (January 1 – December 31).',
    description: 'Pre-K at PS 96 prepares children for kindergarten and beyond through hands-on learning, literacy-rich classrooms, and a strong foundation in math and social skills.',
    highlights: ['18 students per class', 'One head teacher + one paraprofessional', 'Born in 2022 for Fall 2026'],
  },
];

const dates = [
  {
    title: 'Kindergarten',
    note: 'Children born in 2021',
    window: 'December 9 – January 23, 2026',
  },
  {
    title: '3-K & Pre-K',
    note: 'Born in 2023 (3-K) and 2022 (Pre-K 4s)',
    window: 'January 14 – February 27, 2026',
  },
];

const middleSchoolPriorities = [
  'Continuing students',
  'Applicants who have a sibling enrolled at P.S. 96 for the upcoming school year',
  'Students who live within our elementary school zone',
];

const middleSchoolTimeline = [
  { date: 'Wednesday, October 14, 2026', event: 'Middle school application opens', dateTime: '2026-10-14' },
  { date: 'Friday, December 11, 2026', event: 'Middle school application closes', dateTime: '2026-12-11' },
  { date: 'Wednesday, April 7, 2027', event: 'Middle school offers are released', dateTime: '2027-04-07' },
];

const EarlyChildhoodPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">Admissions</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            3-K &amp; Pre-K
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            Thank you for your interest in PS 96, serving grades 3K through 8th.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <a
              href="https://www.myschools.nyc/en/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#fdbb2e] text-[#00213a] font-black rounded-lg hover:bg-[#ffd04e] transition-colors duration-200 shadow-sm"
            >
              Apply on MySchools <ArrowRightIcon />
            </a>
            <a
              href="#middle-school-admissions"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors duration-200"
            >
              Middle School Admissions <ArrowRightIcon />
            </a>
          </div>
        </div>
      </section>

      {/* Welcome — text + image */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-12 mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/5 rounded-lg border border-[#fdbb2e]/30 mb-5">
              <span className="text-sm font-semibold text-[#00213a]">Welcome to PS 96</span>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed mb-5">
              Thank you for your interest in PS 96, serving grades 3K through 8th. PS 96 is a
              District 4 school, which is a zoned district.
            </p>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              To Apply to Kindergarten or 6th Grade
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              NYC families can use MySchools to apply to kindergarten and middle school in the DOE.
            </p>
          </div>
          <div className="h-72 lg:h-96 rounded-2xl overflow-hidden shadow-sm">
            <img
              src="/assets/images/admissions/08e969bc-eeb5-4ea1-ae84-6f97b3ee923f.jpg"
              alt="Students at PS 96"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Program cards — eligibility & class size */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <div className="mb-10">
          <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            3-K &amp; Pre-K Eligibility
          </h2>
          <p className="text-gray-600 mt-3 max-w-3xl text-lg leading-relaxed">
            Children who turn four during the current calendar year (January 1 through December 31) are
            eligible for Pre-K, and children who turn three during the current calendar year are eligible for 3-K.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {programs.map((p) => (
            <div key={p.grade} className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg hover:border-[#fdbb2e]/40 transition-all duration-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-[#00213a] flex items-center justify-center flex-shrink-0">
                  <span className="text-[#fdbb2e] font-black text-lg">{p.grade}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{p.grade}</h3>
                  <p className="text-sm text-gray-500">{p.eligibility}</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">{p.description}</p>
              <ul className="space-y-2">
                {p.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-[#00213a] mt-0.5 flex-shrink-0"><CheckIcon /></span>
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Photo band */}
      <section className="grid grid-cols-1 sm:grid-cols-2">
        <div className="h-72 sm:h-96 overflow-hidden">
          <img
            src="/assets/images/admissions/68c9e6ee-32da-411a-832e-78110842c629.jpg"
            alt="PS 96 early childhood classroom"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="h-72 sm:h-96 overflow-hidden">
          <img
            src="/assets/images/admissions/e5f27136-387d-4908-9e84-078b62925b39.jpg"
            alt="PS 96 students learning"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Fall 2026 Admissions — key dates */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 mb-4">
              <span className="text-sm font-semibold text-gray-700">Application Windows</span>
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              Fall 2026 Admissions
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {dates.map((d) => (
              <div key={d.title} className="bg-white border border-gray-200 rounded-xl p-7">
                <h3 className="text-xl font-semibold text-gray-900 mb-1">{d.title}</h3>
                <p className="text-sm text-gray-500 mb-4">{d.note}</p>
                <div className="inline-flex items-center gap-2 px-3 py-2 bg-[#fdbb2e]/15 rounded-lg">
                  <span className="text-sm font-bold text-[#00213a]">{d.window}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-gray-600 mt-8 leading-relaxed">
            Applications are submitted through{' '}
            <a href="https://www.myschools.nyc/en/" target="_blank" rel="noopener noreferrer" className="text-[#00213a] font-semibold underline hover:text-[#003a5d]">
              MySchools NYC
            </a>
            .
          </p>
        </div>
      </section>

      {/* Middle School Admissions */}
      <section id="middle-school-admissions" className="scroll-mt-28 max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/5 rounded-lg border border-[#fdbb2e]/30 mb-5">
              <span className="text-sm font-semibold text-[#00213a]">6th Grade</span>
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              Middle School Admissions
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Welcome to P.S. 96 Joseph Lanzetta School, located in Manhattan&apos;s District 4! Families applying for
              6th grade must submit an application through the{' '}
              <a href="https://www.myschools.nyc/en/" target="_blank" rel="noopener noreferrer" className="text-[#00213a] font-semibold underline hover:text-[#003a5d]">
                NYC MySchools<span className="sr-only"> (opens in a new tab)</span>
              </a>{' '}
              platform.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Admissions Priorities</h3>
            <p className="text-gray-700 mb-4">Applications are considered in the following order:</p>
            <ol className="space-y-3 mb-10">
              {middleSchoolPriorities.map((item, i) => (
                <li key={item} className="flex items-start gap-3 text-gray-700">
                  <span className="w-7 h-7 rounded-full bg-[#00213a] text-[#fdbb2e] text-sm font-black flex items-center justify-center flex-shrink-0" aria-hidden="true">{i + 1}</span>
                  <span className="pt-0.5">{item}</span>
                </li>
              ))}
            </ol>

            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Visit Our School</h3>
            <p className="text-gray-700 leading-relaxed">
              School tours are a great way to experience P.S. 96 firsthand and learn what makes our school special.
              Tours are led by our students and staff and give families the opportunity to visit classrooms and
              school spaces, meet staff and see our students&apos; learning in action. We look forward to showing you around!
            </p>
          </div>

          <div className="grid gap-6">
            <img
              src="/assets/images/admissions/middle-school-student-project.jpg"
              alt="Three PS 96 middle school students stand beside a display board of their research project in the school gym"
              className="w-full h-auto rounded-2xl shadow-sm"
              loading="lazy"
            />
            <img
              src="/assets/images/admissions/middle-school-groundbreaking.jpg"
              alt="PS 96 students in school polo shirts pose with a ceremonial golden shovel alongside school and city guests at a construction-site groundbreaking"
              className="w-full h-auto rounded-2xl shadow-sm"
              loading="lazy"
            />
          </div>
        </div>

        <div className="mt-16 bg-gray-50 border border-gray-200 rounded-2xl p-8 lg:p-10">
          <h3 className="text-3xl font-semibold text-gray-900 mb-8 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Middle School Admissions Timeline
          </h3>
          <ol className="grid md:grid-cols-3 gap-6">
            {middleSchoolTimeline.map((step) => (
              <li key={step.dateTime} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="inline-flex items-center px-3 py-2 bg-[#fdbb2e]/15 rounded-lg mb-3">
                  <time dateTime={step.dateTime} className="text-sm font-bold text-[#00213a]">{step.date}</time>
                </div>
                <p className="text-lg font-semibold text-gray-900">{step.event}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Contact / enrollment questions */}
      <section className="bg-[#00213a] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-white mb-4 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Enrollment Questions?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Our team is here to help you through every step of the application process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+12128605851"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#fdbb2e] text-[#00213a] font-black rounded-lg hover:bg-[#ffd04e] transition-colors duration-200 shadow-sm"
            >
              <PhoneIcon /> (212) 860-5851
            </a>
            <a
              href="mailto:AMartinezmedrano2@schools.nyc.gov"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors duration-200"
            >
              <MailIcon /> AMartinezmedrano2@schools.nyc.gov
            </a>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default EarlyChildhoodPage;
